/*!
 * vizStyle.js — a line drawn into a box takes the box's viz() style
 * © 2026 Rob Canning — GPLv3
 *
 * viz(s3, stroke:, width:) styles the line drawn in a sound's box. The box
 * keeps that style (data-viz-stroke / data-viz-width), because what is drawn
 * in it does not last: every run of a poly synth draws a new voice's scope,
 * and a re-run can rebuild the old one. Each of those asks here.
 */

/**
 * Colours for the voices drawn in a box, given in turn: viz(s3, palette:…)
 * -- a poly sound's voices, each its own line (one voice: color:). live is
 * the palette boxes used before there was a choice. A list is a palette
 * too, palette:[red, #05d9e8, blue]; palette(mine, [...]) names one, or
 * gives a name here other colours (definePalette).
 */
export const PALETTES = {
  live:    ["#07a", "#c00", "#2a7", "#e80", "#80c", "#089", "#a60", "#c55", "#5a3", "#a3c", "#c80", "#369"],
  grey:    ["#eeeeee", "#9a9a9a", "#cfcfcf", "#6e6e6e", "#b5b5b5", "#858585", "#dedede", "#a8a8a8"],
  primary: ["#e00000", "#0050ff", "#ffd800", "#00a03c", "#ff7a00", "#8a00ff"],
  pastel:  ["#ffb3ba", "#bae1ff", "#ffffba", "#baffc9", "#e0c3fc", "#ffdfba", "#c9f0e1", "#ffc6e5"],
  neon:    ["#ff2a6d", "#05d9e8", "#f6f740", "#7cff6b", "#ff9e00", "#d16bff"],
};
PALETTES.gray = PALETTES.grey;

/** palette(name, [colours]): a palette by that name from now on (on this page). False: not colours. */
export function definePalette(name, colours) {
  const list = [].concat(colours ?? []).map(c => String(c).trim()).filter(Boolean);
  if (!name || !list.length) return false;
  PALETTES[String(name)] = list;
  return true;
}

/**
 * The colours a palette: value names: a list as it is (palette:[red, blue],
 * kept on the box as JSON), or a palette's name. Null: no such palette.
 */
export function paletteOf(v) {
  if (Array.isArray(v)) return v.length ? v.map(String) : null;
  const s = String(v ?? "");
  if (s.startsWith("[")) { try { const a = JSON.parse(s); return Array.isArray(a) && a.length ? a.map(String) : null; } catch { return null; } }
  return PALETTES[s] || null;
}

/**
 * Give `line` the stroke and width its box was styled with, if any. A
 * single stroke: colours every voice; a palette gives each line the next
 * colour, once -- a line keeps its colour for its life, so a re-run of a
 * mono voice does not flicker through the palette.
 */
export function applyVizLine(line, box) {
  const d = box?.dataset;
  if (!line || !d) return;
  const pal = paletteOf(d.vizPalette);
  if (d.vizStroke) line.setAttribute("stroke", d.vizStroke);
  else if (pal) {
    if (line.dataset.palette !== d.vizPalette) {
      const n = box._paletteNext ?? 0;
      box._paletteNext = n + 1;
      line.dataset.palette = d.vizPalette;
      line.dataset.paletteColor = pal[n % pal.length];
    }
    line.setAttribute("stroke", line.dataset.paletteColor);
  }
  if (d.vizWidth) {
    line.setAttribute("stroke-width", d.vizWidth);
    line.setAttribute("vector-effect", "non-scaling-stroke");   // screen pixels
  }
}

/**
 * blend: -- how what is drawn mixes with what is under it (CSS
 * mix-blend-mode): difference flips what it crosses, so overlapping white
 * shapes cut each other out on black; screen adds light, multiply ink.
 * Returns the CSS value, "" for normal / off / 0, or null (with a word)
 * for a mode there is not.
 */
export const BLEND_MODES = [
  "normal", "difference", "exclusion", "screen", "multiply", "overlay", "lighten", "darken",
  "color-dodge", "color-burn", "hard-light", "soft-light", "hue", "saturation", "color", "luminosity",
];
export function blendOf(v) {
  const s = String(v ?? "").replace(/^["']|["']$/g, "").trim().toLowerCase();
  if (["", "normal", "off", "0", "none"].includes(s)) return "";
  if (BLEND_MODES.includes(s)) return s;
  console.warn(`[viz] blend:${s} — one of ${BLEND_MODES.join(", ")}`);
  return null;
}
